/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';

import { BuilderPermission, SalesFolderStatus } from '@gh/builder-shared-data';
import { getFormStateSnapshotValue, queryFormMode, State, UserContext } from '@gh/common-shared';
import { DirtyType, ID } from '@gh/common-shared-data';
import { isEditableFormMode, isEditFormMode, STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import { combineLatest$ } from '@gh/rx';
import { filter$, map$ } from '@gh/rx/operators';

import { first } from 'lodash';
import { Observable } from 'rxjs';

import { LeadSalesFolder } from '../../../resources/lead.model';
import { LeadDetailsCommandService } from '../lead-details-command.service';
import { SfMeetingGridDefinition } from '../lead-details-sf-content/sf-meeting-grid-definition.service';
import {
  LeadDetailsState,
  queryLeadCurrentSalesFolder,
  queryLeadSalesFolderInEditing,
  queryLeadSalesFolders,
  queryLeadIsSalesFolderSelected,
} from '../lead-details.state';
import { ActivatedRoute, Params, Router } from '@angular/router';

@Component({
  standalone: false,
  selector: 'gh-lead-details-sf-tab',
  templateUrl: 'lead-details-sf-tab.component.html',
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SfMeetingGridDefinition],
})
export class LeadDetailsSfTabComponent implements OnInit {
  currentSalesFolderId$: Observable<ID>;
  salesFolderInEditing$: Observable<boolean>;
  salesFolders$: Observable<LeadSalesFolder[]>;
  general$: Observable<LeadSalesFolder>;
  salesFolderCanBeReset$: Observable<boolean>;
  hasSalesFolder$: Observable<boolean>;
  isSalesFolderActive$: Observable<boolean>;

  constructor(private route: ActivatedRoute,
              private state: State<LeadDetailsState>,
              private userContext: UserContext,
              private angRouter: Router,
              private commands: LeadDetailsCommandService,
              public meetingGridDefinition: SfMeetingGridDefinition) {

  }

  get editable(): boolean {
    return isEditableFormMode(this.state.project(queryFormMode));
  }

  get editMode(): boolean {
    return isEditFormMode(this.state.project(queryFormMode));
  }

  get hasPermissionToComplete(): boolean {
    return this.userContext.hasPermission(BuilderPermission.Sales.Leads.COMPLETE);
  }

  ngOnInit(): void {
    const currentSalesFolder$ = this.state.select(queryLeadCurrentSalesFolder).pipe(filter$(Boolean));
    this.hasSalesFolder$ = this.state.select(queryLeadIsSalesFolderSelected);
    this.general$ = currentSalesFolder$.pipe(map$((current: any) => getFormStateSnapshotValue(current.general)));
    this.currentSalesFolderId$ = currentSalesFolder$.pipe(map$((current: any) => current.id));
    this.salesFolderInEditing$ = this.state.select(queryLeadSalesFolderInEditing);
    this.isSalesFolderActive$ = this.general$.pipe(map$(({ status }) => status === SalesFolderStatus.Active));
    this.salesFolders$ = this.state.select(queryLeadSalesFolders);

    this.salesFolderCanBeReset$ = combineLatest$([this.salesFolders$, this.salesFolderInEditing$]).pipe(
      map$(([salesFolders, salesFolderInEditing]) =>
        // sales folder can be reset only when it is not the first sales folder
        salesFolderInEditing
        && (salesFolders.length !== 1 || first(salesFolders)!.dirtyType !== DirtyType.New)));
  }

  onSalesFolderAdd(): void {
    this.commands.salesFolderAdd();
  }

  onSalesFolderEdit(): void {
    this.commands.salesFolderEdit();
  }

  onSalesFolderReset(): void {
    this.commands.salesFolderReset();
  }

  onSalesFolderChange(folderId: ID): void {
    const queryParams: Params = { salesFolderId: folderId };
    this.angRouter.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'merge',
      },
    );
    this.commands.salesFolderChange(folderId);
  }

  onCompleteSale(): void {
    const sf = this.state.project(queryLeadCurrentSalesFolder);

    if (sf) {
      this.commands.completeSale(sf.id);
    }
  }

  onFirstContactLetter(): void {
    this.commands.produceFirstContactLetter(this.state.project(queryLeadCurrentSalesFolder)!.id);
  }

  onAuthorityToProceed(): void {
    const sf = this.state.project(queryLeadCurrentSalesFolder);

    if (sf) {
      this.commands.produceAuthorityToProceed(sf.id);
    }
  }

  onMemoChange(memo: string): void {
    this.commands.setMemo(memo);
  }
}
